{
  "blocks": [
    {
      "id": "test-preimplantation-genetic-testing",
      "kind": "test",
      "tags": ["testing", "both-partners", "ivf", "during-cycle", "genomic"],
      "title": "Preimplantation Genetic Testing",
      "subtitle": "Embryo genetic testing before transfer",
      "icon": "🧬",
      "body": {
        "testName": "Preimplantation Genetic Testing (PGT)",
        "category": "genetic",
        "evidenceStrength": "mixed",
        "whatItMeasures": "Preimplantation genetic testing is an umbrella category for embryo genetic testing during IVF.\n\nDifferent PGT types answer different questions:\nPGT-A looks at chromosome number.\nPGT-M looks for a known single-gene condition.\nPGT-SR looks for unbalanced structural chromosome rearrangements.\nPGT-P estimates relative risk for some polygenic conditions.\n\nNewer whole-genome embryo platforms can combine several of these outputs from the same embryo biopsy, so PGT-A may already be included rather than purchased as a separate test.",
        "howItWorks": "Most PGT starts with embryo biopsy at the blastocyst stage. An embryologist removes a small number of trophectoderm cells, embryos are usually frozen, and the biopsy sample is sent to a genetics lab. If the biopsy is already being done, the practical question is how much useful genetic information the lab can return from that same sample. Some platforms return PGT-A, PGT-M or gene-panel findings, PGT-SR, polygenic risk scores, parental confirmation, and other checks in one report.",
        "howItWorksSteps": [
          "Embryos grow in the IVF lab to the blastocyst stage.",
          "An embryologist biopsies a small number of cells from the trophectoderm, which becomes placenta tissue.",
          "Embryos are usually vitrified while the genetics lab runs the test.",
          "The lab reports the PGT result type, such as chromosome status, single-gene status, structural rearrangement status, or polygenic risk score.",
          "Your clinic reviews which pieces are included in the report, so you do not accidentally pay twice for PGT-A or miss information that could have come from the same biopsy.",
          "Transfer decisions should use the result together with embryo quality, family context, and counseling."
        ],
        "quickFacts": [
          {
            "kind": "sample",
            "value": "Embryo biopsy cells"
          },
          {
            "kind": "time",
            "value": "No extra patient procedure"
          },
          {
            "kind": "pain",
            "value": "None for patient"
          },
          {
            "kind": "anesthesia",
            "value": "None for the biopsy"
          },
          {
            "kind": "prep",
            "value": "Requires IVF and blastocysts"
          },
          {
            "kind": "results",
            "value": "Often 1-2 weeks"
          }
        ],
        "expectations": {
          "canExpect": [
            "A lab report that answers a specific genetic question for each embryo tested",
            "A broader report may include PGT-A-style aneuploidy screening plus additional embryo genomic information from the same biopsy",
            "Different usefulness depending on the indication and PGT type",
            "Genetic counseling when results are complex, inherited, mosaic, uncertain, or used for embryo prioritization"
          ],
          "shouldNotExpect": [
            "Every lab to include the same layers of testing, even when they use similar terms",
            "A guarantee of implantation, live birth, or a child without health conditions",
            "PGT-P risk scores to have the same clinical certainty as PGT-M for a known high-risk single-gene condition"
          ]
        },
        "considerations": [
          "Ask which PGT type is being recommended and why: PGT-A, PGT-M, PGT-SR, PGT-P, or a combination.",
          "Ask whether PGT-A is already included in the broader embryo sequencing report before paying for it separately.",
          "If a platform is described like Orchid, Herasight, or another broader embryo-genomics workflow, ask for a plain list of included outputs: PGT-A, PGT-M or gene panels, PGT-SR, PGT-P, parental confirmation, and any extra chromosome findings.",
          "Ask whether the same biopsy can return more information, because cost and lab access are usually the main reasons to limit the report once biopsy is already happening.",
          "Ask whether the result changes transfer order, rules out specific embryos, or mainly adds information.",
          "Ask how the clinic handles mosaic, no-result, carrier, uncertain, and lower-priority embryos before testing.",
          "Ask whether a genetic counselor can review the plan before biopsy and again after results."
        ],
        "bestFor": [
          "PGT-M when there is a known reproductive risk for a serious single-gene condition",
          "PGT-SR when a parent carries a structural chromosome rearrangement",
          "PGT-A when chromosome screening may help prioritize embryos, especially with multiple blastocysts",
          "PGT-P when embryo biopsy is already happening and you want broader risk information from the same sample, especially if cost and lab access are acceptable"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "ESHRE groups PGT guidance into general PGT practice plus technical guidance for biopsy, PGT-M, PGT-SR, and PGT-A",
            "mechanism": "PGT is not one test. Each subtype has different indications, limitations, and evidence.",
            "source": "ESHRE PGT good practice recommendations, 2020"
          },
          {
            "direction": "positive",
            "outcome": "PGT-M is an established option when a significant monogenic reproductive risk is identified",
            "mechanism": "The clinical question is targeted: whether an embryo appears affected by a known familial variant or inheritance pattern.",
            "source": "ASRM PGT-M committee opinion, 2023"
          },
          {
            "direction": "neutral",
            "outcome": "PGT-A evidence is mixed and depends on patient context, embryo yield, age, and the outcome being measured",
            "source": "ASRM PGT-A committee opinion, 2024"
          },
          {
            "direction": "negative",
            "outcome": "PGT-P (polygenic risk scoring) is not recommended for clinical use - ASRM Ethics Committee 2026 explicitly recommends against offering it as a clinical service",
            "mechanism": "Polygenic embryo selection is unproven, validated mostly in European-ancestry populations, and has not shown clinical benefit in any RCT. It is also illegal in France, Germany, and several other European countries.",
            "source": "ASRM PGT-P ethics committee opinion, 2026"
          }
        ],
        "headlineStat": "Umbrella category",
        "atHome": false,
        "clinicRequired": false,
        "dataPoints": [],
        "section": "genetic-screening"
      },
      "sources": [
        {
          "title": "Good practice recommendations for preimplantation genetic testing",
          "journal": "ESHRE PGT Consortium",
          "year": 2020,
          "url": "https://www.eshre.eu/Guidelines-and-Legal/Guidelines/PGT",
          "evidenceLevel": "guideline"
        },
        {
          "title": "Indications and management of preimplantation genetic testing for monogenic conditions",
          "journal": "ASRM Practice Committee",
          "year": 2023,
          "url": "https://www.asrm.org/practice-guidance/practice-committee-documents/indications-and-management-of-preimplantation-genetic-testing-for-monogenic-conditions-a-committee-opinion-2023/",
          "evidenceLevel": "guideline"
        },
        {
          "title": "The use of preimplantation genetic testing for aneuploidy",
          "journal": "ASRM Practice Committee",
          "year": 2024,
          "url": "https://www.asrm.org/practice-guidance/practice-committee-documents/the-use-of-preimplantation-genetic-testing-for-aneuploidy-a-committee-opinion-2024/",
          "evidenceLevel": "guideline"
        },
        {
          "title": "Use of preimplantation genetic testing for polygenic disorders (PGT-P): an Ethics Committee opinion",
          "authors": "Ethics Committee of the American Society for Reproductive Medicine",
          "journal": "Fertility and Sterility",
          "year": 2026,
          "url": "https://www.asrm.org/practice-guidance/ethics-opinions/use-of-preimplantation-genetic-testing-for-polygenic-disorders-pgt-p-an-ethics-committee-opinion-2026/",
          "evidenceLevel": "guideline"
        },
        {
          "title": "Preimplantation Genetic Testing (Committee Opinion No. 799)",
          "authors": "American College of Obstetricians and Gynecologists",
          "journal": "Obstetrics & Gynecology",
          "year": 2020,
          "url": "https://pubmed.ncbi.nlm.nih.gov/32080053/",
          "evidenceLevel": "expert-opinion"
        }
      ],
      "readTimeMinutes": 5
    }
  ],
  "relationships": [
    {
      "from": "test-preimplantation-genetic-testing",
      "to": "test-pgt-a",
      "type": "related-to",
      "label": "Subtype"
    },
    {
      "from": "test-preimplantation-genetic-testing",
      "to": "test-pgt-m",
      "type": "related-to",
      "label": "Subtype"
    },
    {
      "from": "test-preimplantation-genetic-testing",
      "to": "test-pgt-sr",
      "type": "related-to",
      "label": "Subtype"
    },
    {
      "from": "test-preimplantation-genetic-testing",
      "to": "test-pgt-p",
      "type": "related-to",
      "label": "Subtype"
    }
  ],
  "localeVariants": []
}
